<template>
  <div id="add-update-mailbox-provider" class="pt-8">
    <v-form
      v-model="isFormValid"
      ref="addMailboxProvider"
      @submit.prevent="submitHandler"
    >
      <card-widget :card-title="title">
        <template #cardBody>
          <v-row>
            <v-col cols="12" lg="6" md="6">
              <v-text-field
                outlined
                class="required"
                v-model="formData.name"
                label="Mailbox Provider"
                :rules="[
                  required('Mailbox Provider'),
                  alphaNumeric('Mailbox Provider'),
                ]"
              />
            </v-col>
            <v-col cols="12" lg="6" md="6">
              <v-combobox
                outlined
                ref="region"
                label="Region"
                class="required"
                :items="regions"
                v-model="formData.region"
                :loading="areRegionsLoading"
                :search-input.sync="newRegion"
                @input.native="$refs.region.validate()"
                @update:search-input="$refs.region.validate()"
                :rules="[required('Region'), alphaNumeric('Region')]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" lg="6" md="6">
              <v-text-field
                outlined
                class="required"
                label="IMAP Host"
                v-model="formData.imap_host"
                :rules="[required('IMAP Host')]"
              />
            </v-col>
            <v-col cols="12" lg="6" md="6">
              <v-select
                outlined
                item-text="name"
                item-value="value"
                label="Access Type"
                :items="accessTypes"
                :rules="[requiredAccess]"
                class="required select-dropdown"
                item-color="primary custom-dropdown"
                v-model.number="formData.access_type"
                :menu-props="{ bottom: true, offsetY: true }"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" lg="6" md="6">
              <v-text-field
                v-model="formData.inbox_label"
                :rules="[required('Inbox Label'), alphaNumeric('Inbox Label')]"
                label="Inbox Label"
                class="required"
                outlined
              />
            </v-col>
            <v-col cols="12" lg="6" md="6">
              <v-text-field
                outlined
                class="required"
                label="Spam Label"
                v-model="formData.spambox_label"
                :rules="[required('Spam Label')]"
              />
            </v-col>
          </v-row>
          <loader :loading="showLoader" :size="$appConfig.loader.small" />
        </template>
      </card-widget>
      <v-divider class="my-8 border-light-grey" />
      <v-row class="mb-8">
        <v-col>
          <v-btn
            type="submit"
            color="primary"
            :loading="isLoading"
            :disabled="!isFormValid"
            class="px-4 button font-weight-bold white-btn-text"
          >
            Save
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import CardWidget from "@/components/widgets/CardWidget.vue";
import {
  required,
  requiredNumber,
  alphaNumeric,
  letterOnly,
} from "@/validators/form-validators";
import {
  createMailboxProvider,
  getMailboxProviders,
  updateMailboxProvider,
} from "@/services";
import {
  isEmpty,
  uniqByProp,
  onlyNumber,
  pick,
  isResponseCode,
  hasProp,
  defer,
} from "@/utils/common.utils";
import { ACCESS_TYPES, RESPONSE_CODES } from "@/constants";
import { SharedUtilsMixin } from "@/mixins";

/**
 * Add edit mailbox provider detail
 */
export default {
  name: "AddEditMailboxProvider",
  /**
  |--------------------------------------------------
  | Custom event
  |--------------------------------------------------
  */
  emits: ["update-provider-name"],
  /**
  |--------------------------------------------------
  | Props
  |--------------------------------------------------
  */
  props: {
    mailboxProviderDetails: { type: Object, default: () => ({}) },
    isContentLoading: { type: Boolean, default: false },
  },
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [SharedUtilsMixin],
  /**
  |--------------------------------------------------
  | Components
  |--------------------------------------------------
  */
  components: {
    CardWidget,
  },
  /**
  |--------------------------------------------------
  | Watching properties
  |--------------------------------------------------
  */
  watch: {
    /**
     * Watches mailboxProviderDetails prop and prepopulates form data
     */
    mailboxProviderDetails(val) {
      this.prePopulateForm(val);
    },
  },
  /**
  |--------------------------------------------------
  | Computed properties
  |--------------------------------------------------
  */
  computed: {
    showLoader() {
      return this.isLoading || this.areRegionsLoading || this.isContentLoading;
    },
    /**
     * Determines that mailbox provider needs to be updated or not
     * @type {Boolean}
     */
    isEditMailbox() {
      return !!this.$route.params.id;
    },
    /**
     * Title of the mailbox provider form
     */
    title() {
      const { addMailbox } = this.$dictionary.app.mailboxProvider;
      return addMailbox.formTitle;
    },
    /**
     * Create and update mailbox provider api request payload
     * @type {Object}
     *
     */
    payload() {
      const props = [
        "name",
        "region",
        "imap_host",
        "inbox_label",
        "spambox_label",
        "access_type",
      ];

      return pick(this.formData, props);
    },
    /**
     * Determines whether mailbox provider region entered by the user is new or not
     * @type {Boolean}
     */
    isNewRegion() {
      if (!this.newRegion) return false;
      return (
        this.regions.findIndex(
          (reg) => this.newRegion.toLowerCase() === reg.toLowerCase()
        ) <= -1
      );
    },
  },
  /**
  |--------------------------------------------------
  | Data Properties
  |--------------------------------------------------
  */
  data() {
    return {
      isLoading: false,
      isFormValid: false,
      mailboxProviders: [],
      regions: [],
      areRegionsLoading: false,
      accessTypes: [...ACCESS_TYPES],
      formData: { access_type: ACCESS_TYPES[0].value },
      newRegion: "",
      mailboxProvider: {},
    };
  },
  /**
  |--------------------------------------------------
  | Method
  |--------------------------------------------------
  */
  methods: {
    required,
    onlyNumber,
    letterOnly,
    alphaNumeric,
    requiredNumber,
    requiredAccess(v) {
      return v === 0 || !!v || "Access Type is required.";
    },
    /**
     * @emits update-provider-name in parent component with updated provider name
     */
    updateProviderName(data) {
      this.$emit("update-provider-name", data);
    },
    /**
     * Pre populates form data in edit mailbox provider mode
     */
    prePopulateForm(data) {
      // Populates mailbox form data if it is in edit mode
      if (!isEmpty(data) && this.isEditMailbox) {
        this.formData = { ...data };
      }
    },
    /**
     * Computes mailbox provider service on the basis of {{isEditMailbox}} property
     * @returns {Promise} Service method to be called
     */
    mailboxProviderService() {
      const { id } = this.formData;
      const region = this.isNewRegion ? this.newRegion : this.payload.region;
      return this.isEditMailbox
        ? updateMailboxProvider({ id, ...this.payload, region })
        : createMailboxProvider({ ...this.payload, region });
    },
    /**
     * Handles add and update mailbox provider form
     * @listens submit add edit mailbox provider form events
     */
    async submitHandler() {
      try {
        this.$refs.addMailboxProvider.validate();
        let message = this.$dictionary.errors.common.msg;
        if (!this.isFormValid) return this.$refs.addMailboxProvider.validate();
        this.isLoading = true;
        const { status, data } = await this.mailboxProviderService();

        // Shows success message if mailbox provider have been created
        if (isResponseCode(status, RESPONSE_CODES.success)) {
          message = this.successMessage(this.payload.name);
        }
        this.setSnackbar({
          value: true,
          message,
          type: this.snackbarTypes.success,
        });
        this.updateProviderName(data);
        // Redirect user to mailbox providers index page if new mailbox provider is created
        if (!this.isEditMailbox) {
          defer(() => this.$router.push(this.$paths.mailboxProviders), 1000);
        }
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Computes custom error messages and displayes them to the user in snackbar
     * @param {Error} error Api error object
     */
    showErrorMessage(error) {
      if (error?.response && error.response.data) {
        const { data } = error.response;
        if (hasProp(data, "name")) {
          this.setSnackbar({
            value: true,
            message: this.$dictionary.app.mailboxProvider.addMailbox
              .duplicateMailbox,
            type: this.snackbarTypes.error,
          });
        }
      }
    },
    /**
     * Success message to be shown to the user
     * @returns {String} Success message
     */
    successMessage(name) {
      const {
        addMailbox,
        updateMailbox,
      } = this.$dictionary.app.mailboxProvider;

      let message = this.isEditMailbox
        ? updateMailbox.successMsg
        : addMailbox.successMsg;
      return message.replace(/<<name>>/gi, name);
    },
    /**
     * Fetches mailbox providers and computed the regions from mailbox providers
     */
    async getMailboxProvidersList() {
      try {
        this.areRegionsLoading = true;
        const { data } = await getMailboxProviders();
        this.regions = !isEmpty(data) ? uniqByProp(data, "region") : [];
      } finally {
        this.areRegionsLoading = false;
      }
    },
  },
  /**
  |--------------------------------------------------
  | Created lifecycle hook
  |--------------------------------------------------
  */
  created() {
    this.getMailboxProvidersList();
    this.prePopulateForm(this.mailboxProviderDetails);
  },
};
</script>
